import React from 'react';
import styled from 'styled-components';
import { Building2, Users } from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #008752 0%, #006B41 50%, #004D30 100%);
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  width: 100%;
  max-width: 500px;
  text-align: center;
`;

const LogoImg = styled.img`
  height: 80px;
  width: auto;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
  display: block;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const OptionButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 2px solid ${({ theme, $variant }) => 
    $variant === 'primary' ? theme.colors.primary : theme.colors.gray300};
  background: ${({ theme, $variant }) => 
    $variant === 'primary' ? theme.colors.primary + '10' : theme.colors.white};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}15;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const IconWrapper = styled.div<{ $variant: 'primary' | 'secondary' }>`
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme, $variant }) => 
    $variant === 'primary' ? theme.colors.primary : theme.colors.gray100};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $variant }) => 
    $variant === 'primary' ? theme.colors.white : theme.colors.textSecondary};
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const OptionDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

interface UserTypeSelectionProps {
  onSelectType: (type: 'externo' | 'interno') => void;
}

const UserTypeSelection: React.FC<UserTypeSelectionProps> = ({ onSelectType }) => {
  return (
    <Container>
      <Card>
        <LogoImg src="/logo-alianza-team.png" alt="Alianza Team" />
        <Title>COMEX</Title>
        <Subtitle>Seleccione su tipo de usuario</Subtitle>

        <OptionsContainer>
          <OptionButton $variant="primary" onClick={() => onSelectType('externo')}>
            <IconWrapper $variant="primary">
              <Users size={24} />
            </IconWrapper>
            <OptionContent>
              <OptionTitle>Proveedor Externo</OptionTitle>
              <OptionDescription>
                Empresas de transporte y proveedores de servicios logísticos
              </OptionDescription>
            </OptionContent>
          </OptionButton>

          <OptionButton $variant="secondary" onClick={() => onSelectType('interno')}>
            <IconWrapper $variant="secondary">
              <Building2 size={24} />
            </IconWrapper>
            <OptionContent>
              <OptionTitle>Usuario Interno</OptionTitle>
              <OptionDescription>
                Personal de Alianza Team (acceso con Azure AD)
              </OptionDescription>
            </OptionContent>
          </OptionButton>
        </OptionsContainer>
      </Card>
    </Container>
  );
};

export default UserTypeSelection;

